import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArchive = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedPosts, setGroupedPosts] = useState({});

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/seo/blog/posts?status=published`);
      const fetchedPosts = response.data.posts || [];
      setPosts(fetchedPosts);
      
      // Group posts by year and month
      const grouped = fetchedPosts.reduce((acc, post) => {
        const date = new Date(post.published_at);
        const year = date.getFullYear();
        const month = date.toLocaleString('default', { month: 'long' });
        const key = `${year}-${month}`;
        
        if (!acc[year]) acc[year] = {};
        if (!acc[year][month]) acc[year][month] = [];
        acc[year][month].push(post);
        
        return acc;
      }, {});
      
      setGroupedPosts(grouped);
    } catch (err) {
      setError('Failed to load blog archive');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <Helmet>
          <title>Dental News Archive | Fort Worth Dental</title>
          <meta name="description" content="Browse our complete archive of dental health articles, tips, and insights from Fort Worth Dental." />
        </Helmet>
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading blog archive...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dental News Archive | Fort Worth Dental | All Articles</title>
        <meta name="description" content="Browse our complete archive of dental health articles, tips, and insights from Fort Worth Dental. Find expert advice on dental implants, Invisalign, cosmetic dentistry, and more." />
        <meta name="keywords" content="dental blog archive, fort worth dentist articles, dental health tips archive, dental implant blog" />
        <link rel="canonical" href="https://fortworthdental.com/blog/archive" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Dental News Archive</h1>
          <p className="text-xl">Browse All Our Dental Health Articles</p>
          <Link 
            to="/blogs" 
            className="inline-block mt-6 text-teal-200 hover:text-white"
          >
            ← Back to Latest Posts
          </Link>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {posts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md p-8">
            <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Coming Soon!</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              We're working on building a library of helpful dental health articles, tips, and insights. 
              Check back soon for expert advice from Dr. Mund and Dr. Razo!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact-us" 
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Contact Us
              </Link>
              <a 
                href="tel:8179853337" 
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-6 py-3 rounded-lg font-semibold"
              >
                Call (817) 985-3337
              </a>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Archive Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Archive</h2>
                <nav>
                  {Object.keys(groupedPosts).sort((a, b) => b - a).map(year => (
                    <div key={year} className="mb-4">
                      <h3 className="font-bold text-teal-600 mb-2">{year}</h3>
                      <ul className="space-y-1">
                        {Object.keys(groupedPosts[year]).map(month => (
                          <li key={`${year}-${month}`}>
                            <a 
                              href={`#${year}-${month}`}
                              className="text-gray-600 hover:text-teal-600 text-sm flex items-center justify-between"
                            >
                              <span>{month}</span>
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                {groupedPosts[year][month].length}
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </nav>
                
                {/* Topics */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-bold text-gray-800 mb-3">Popular Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium">Dental Implants</span>
                    <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium">Invisalign</span>
                    <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium">Oral Health</span>
                    <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium">Cosmetic</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - Posts by Date */}
            <div className="lg:col-span-3">
              {Object.keys(groupedPosts).sort((a, b) => b - a).map(year => (
                <div key={year} className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-2 border-teal-500 pb-2">{year}</h2>
                  
                  {Object.keys(groupedPosts[year]).map(month => (
                    <div key={`${year}-${month}`} id={`${year}-${month}`} className="mb-8">
                      <h3 className="text-xl font-semibold text-teal-600 mb-4">{month}</h3>
                      <div className="space-y-4">
                        {groupedPosts[year][month].map(post => (
                          <article 
                            key={post.id}
                            className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center">
                                <span className="text-2xl font-bold text-teal-600">
                                  {new Date(post.published_at).getDate()}
                                </span>
                              </div>
                              <div className="flex-grow">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs font-semibold">
                                    {post.topic}
                                  </span>
                                  <span className="text-gray-400 text-sm">
                                    {new Date(post.published_at).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                <h4 className="text-lg font-bold text-gray-800 hover:text-teal-600 mb-2">
                                  <Link to={`/blogs/${post.slug}`}>{post.title}</Link>
                                </h4>
                                <p className="text-gray-600 text-sm line-clamp-2 mb-2">{post.excerpt}</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-500">By {post.author}</span>
                                  <Link 
                                    to={`/blogs/${post.slug}`}
                                    className="text-teal-600 text-sm font-semibold hover:underline"
                                  >
                                    Read Article →
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <section className="mt-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-lg text-center">
          <h2 className="text-2xl font-bold mb-4">Have Questions About Your Dental Health?</h2>
          <p className="mb-6">Our experienced team is here to help. Schedule a consultation today!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:8179853337" 
              className="bg-white text-teal-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 inline-block"
            >
              Call (817) 985-3337
            </a>
            <Link 
              to="/contact-us"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white/10 inline-block"
            >
              Contact Us Online
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogArchive;
